import { useEffect, useRef } from 'preact/hooks';
import { FooterLinks } from '@ts/services/footerService';
import { useMediaQuery, useToggle } from 'usehooks-ts';
import { Icon } from '../Icon/Icon';

export const calculateDuration = (height: number) => {
  const baseSpeed = 2;
  const minHeight = 200; // Element's min height to apply `smooth speed`
  const speedAdjustmentFactor = 0.03; // Adjust `smooth speed` for shorter elements

  // Calculate adjusted speed based on height
  const adjustedSpeed =
    baseSpeed + (minHeight - height) * speedAdjustmentFactor;
  const duration = height * Math.max(adjustedSpeed, baseSpeed);

  return duration;
};

export const applyStyles = (
  target: HTMLDivElement,
  { maxHeight = '0px', marginBottom = '0px', transition = '0ms' },
) => {
  Object.assign(target.style, {
    transition,
    maxHeight,
    marginBottom,
  });
};

type NavItemProps = {
  title: string;
  links: FooterLinks[] | undefined;
};

export const NavTopic = ({ title, links }: NavItemProps) => {
  const navItemsRef = useRef<HTMLDivElement>(null);
  const [isOpen, toggleIsOpen, setIsOpen] = useToggle(false);
  const matchesLargeMedia = useMediaQuery('(min-width: 760px)', {
    initializeWithValue: true,
  }); // Mid-tablet media query

  const handleToggleNav = () => {
    if (matchesLargeMedia) return;

    const target = navItemsRef.current;
    if (!target) return;

    const height = `${target.scrollHeight}px`;
    const duration = calculateDuration(target.scrollHeight);

    const styles = isOpen
      ? {
          transition: `all ${duration}ms ease-in-out`,
        }
      : {
          maxHeight: height,
          marginBottom: '30px',
          transition: `all ${duration}ms ease-in-out`,
        };

    applyStyles(target, styles);

    toggleIsOpen();
  };

  const resetToggleNav = () => {
    const target = navItemsRef.current;
    if (!target) return;

    const styles = matchesLargeMedia
      ? { maxHeight: '100%', marginBottom: '30px' }
      : {};

    applyStyles(target, styles);

    setIsOpen(() => matchesLargeMedia);
  };

  useEffect(() => {
    resetToggleNav();
  }, [matchesLargeMedia]);

  return (
    <nav
      className="bc-footer__nav-topic"
      aria-label={`Footer navigation: ${title}`}
    >
      <div className="bc-footer__nav-title" onClick={handleToggleNav}>
        {title}

        <Icon
          iconName={isOpen ? 'chevron-up' : 'chevron-down'}
          width={20}
          height={20}
          title="Navigation umschalten"
        />
      </div>
      <div
        className="bc-footer__nav-items"
        ref={navItemsRef}
        data-testid={`${title}-items`}
      >
        {links?.map((link, index) => (
          <a key={index} href={link.url} className="bc-footer__nav-link">
            {link.title}
          </a>
        ))}
      </div>
    </nav>
  );
};
